// ═══════════════════════════════════════════════════════════════
// PACE — Dashboard
// Stats, completeness ring, recent call-downs, quick links
// ═══════════════════════════════════════════════════════════════

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Title, Text, SimpleGrid, Paper, Group, Stack, Button, Box,
  RingProgress, Center, ThemeIcon, Skeleton, Progress,
} from '../../components/gab/GabComponents';
import { IconAntenna, IconPhone, IconFileText, IconChartBar,
  IconActivity, IconArrowRight, IconUsers } from '@tabler/icons-react';
import { useAuthStore } from '../../store/authStore';
import { usePaceStore } from '../../store/paceStore';

export default function PaceDashboard() {
  const navigate = useNavigate();
  const { activeIncident } = useAuthStore();
  const { profiles, callDowns, plans, loading, loadProfiles, loadCallDowns, loadPlans } = usePaceStore();

  useEffect(() => {
    loadProfiles();
    loadPlans();
    if (activeIncident) {
      loadCallDowns(String(activeIncident.id));
    }
  }, [activeIncident?.id]);

  if (!activeIncident) {
    return (
      <Center py={80}>
        <Stack align="center" gap="sm">
          <ThemeIcon size={60} radius="xl" variant="light" color="gray">
            <IconAntenna size={30} />
          </ThemeIcon>
          <Title order={3}>No Incident Selected</Title>
          <Text size="sm" c="dimmed" ta="center" maw={420}>
            PACE communications are scoped to an incident. Pick one from the
            incident selector in the top bar, or visit the Incident Hub.
          </Text>
          <Group mt="sm">
            <Button variant="light" onClick={() => navigate('/incidents')}>Incident Hub</Button>
            <Button variant="subtle" onClick={() => navigate('/pace/profile')}>Edit My Profile</Button>
          </Group>
        </Stack>
      </Center>
    );
  }

  // Profile stats — V2 model: one profile per person, tiers are fields (primary/alternate/contingency/emergency)
  const uniquePersons = profiles.length;
  const compliantPersons = profiles.filter(p =>
    p.primary?.trim() && p.alternate?.trim() && p.contingency?.trim() && p.emergency?.trim()
  ).length;
  const partialPersons = profiles.filter(p => {
    const filled = [p.primary, p.alternate, p.contingency, p.emergency].filter(v => v?.trim()).length;
    return filled > 0 && filled < 4;
  }).length;
  const compliancePct = uniquePersons > 0 ? Math.round((compliantPersons / uniquePersons) * 100) : 0;

  const activeCallDowns = callDowns.filter(cd => cd.callStatus === 'active');

  const ringColor = compliancePct >= 80 ? '#16a34a' : compliancePct >= 50 ? '#d97706' : '#dc2626';

  const statCards = [
    { label: 'Total Personnel', value: uniquePersons, icon: IconUsers, color: '#627d98' },
    { label: 'Fully Compliant', value: `${compliantPersons} (${compliancePct}%)`, icon: IconActivity, color: '#059669' },
    { label: 'Active Call-Downs', value: activeCallDowns.length, icon: IconPhone, color: '#2563eb' },
    { label: 'Partial / Incomplete', value: partialPersons, icon: IconFileText, color: '#d97706' },
  ];

  return (
    <div>
      {/* Header */}
      <Group justify="space-between" mb="lg">
        <div>
          <Title order={3} c="black">PACE Communications</Title>
          <Text size="sm" c="dimmed">
            {activeIncident.name} — Primary, Alternate, Contingent, Emergency
          </Text>
        </div>
      </Group>

      {/* Org Compliance Summary */}
      <Paper p="xs" mb="sm" withBorder style={{
        borderColor: compliancePct >= 80 ? '#bbf7d0' : compliancePct >= 50 ? '#fde68a' : '#fecaca',
        background: compliancePct >= 80 ? '#f0fdf4' : compliancePct >= 50 ? '#fffbeb' : '#fef2f2',
      }}>
        <Group gap="sm" wrap="nowrap">
          <RingProgress
            size={48} thickness={4} roundCaps
            sections={[{ value: compliancePct, color: ringColor }]}
            label={
              <Text ta="center" fw={700} size="xs" style={{ color: ringColor }}>
                {compliancePct}%
              </Text>
            }
          />
          <Box style={{ flex: 1 }}>
            <Text fw={600} size="sm" style={{
              color: compliancePct >= 80 ? '#166534' : compliancePct >= 50 ? '#92400e' : '#991b1b',
            }}>
              {compliancePct >= 80 ? 'Strong PACE Compliance'
               : compliancePct >= 50 ? 'Partial Compliance — Action Needed'
               : 'Low Compliance — Profiles Incomplete'}
            </Text>
            <Text size="xs" style={{ color: compliancePct >= 80 ? '#15803d' : compliancePct >= 50 ? '#a16207' : '#b91c1c' }}>
              {compliantPersons} of {uniquePersons} personnel have all 4 tiers · {partialPersons} partially complete
            </Text>
          </Box>
          <Button variant="light" color="cyan" size="compact-xs" rightSection={<IconArrowRight size={12} />}
            onClick={() => navigate('/pace/directory')}>
            View All
          </Button>
        </Group>
      </Paper>

      {/* Stat Cards */}
      {loading ? (
        <SimpleGrid cols={{ base: 2, sm: 4 }} mb="md">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} height={100} radius="sm" />)}
        </SimpleGrid>
      ) : (
        <SimpleGrid cols={{ base: 2, sm: 4 }} mb="md" spacing="sm">
          {statCards.map(s => (
            <Paper key={s.label} p="sm" radius="sm" withBorder ta="center"
              style={{ borderTop: `3px solid ${s.color}` }}>
              <Text size="xs" c="dimmed" fw={600}>{s.label}</Text>
              <Text fw={700} fz={28} style={{ color: s.color }}>{s.value}</Text>
            </Paper>
          ))}
        </SimpleGrid>
      )}

      {/* Compliance Bar */}
      {uniquePersons > 0 && (
        <Paper p="lg" withBorder mb="md">
          <Group justify="space-between" mb="sm">
            <Text fw={600}>Overall Compliance</Text>
            <Text size="sm" fw={700}
              c={compliancePct >= 80 ? 'green' : compliancePct >= 50 ? 'yellow.7' : 'red'}>
              {compliancePct}%
            </Text>
          </Group>
          <Progress
            value={compliancePct}
            color={compliancePct >= 80 ? 'green' : compliancePct >= 50 ? 'yellow' : 'red'}
            size="md" radius="xl"
          />
          <Text size="xs" c="dimmed" mt="xs">
            Compliance = all 4 PACE tiers filled in per person
          </Text>
        </Paper>
      )}

      {/* Quick Links */}
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
        <QuickLink
          icon={<IconAntenna size={18} />}
          iconBg="#cffafe"
          title="My PACE Profile"
          subtitle={`${compliantPersons} of ${uniquePersons} fully complete`}
          onClick={() => navigate('/pace/my-pace')}
        />
        <QuickLink
          icon={<IconPhone size={18} />}
          iconBg="#dbeafe"
          title="Call-Downs"
          subtitle={`${activeCallDowns.length} active`}
          onClick={() => navigate('/pace/calldowns')}
        />
        <QuickLink
          icon={<IconFileText size={18} />}
          iconBg="#ede9fe"
          title="PACE Plans"
          subtitle={`${plans.length} plan${plans.length !== 1 ? 's' : ''}`}
          onClick={() => navigate('/pace/plans')}
        />
        <QuickLink
          icon={<IconChartBar size={18} />}
          iconBg="#fef3c7"
          title="Reports"
          subtitle="Compliance & contact sheets"
          onClick={() => navigate('/pace/reports')}
        />
      </SimpleGrid>

      {/* PACE Primer */}
      <Paper p="md" mt="lg" withBorder style={{ background: '#f8fafc' }}>
        <Text fw={600} size="sm" c="dimmed" mb="xs">What is PACE?</Text>
        <Stack gap={6}>
          <Group gap={8}><Text span fw={700} size="xs" c="green" w={70}>Primary</Text><Text span size="xs" c="dimmed">Your first-choice method under normal conditions.</Text></Group>
          <Group gap={8}><Text span fw={700} size="xs" c="blue" w={70}>Alternate</Text><Text span size="xs" c="dimmed">Backup if primary fails. Should be a different method.</Text></Group>
          <Group gap={8}><Text span fw={700} size="xs" c="yellow.7" w={70}>Contingent</Text><Text span size="xs" c="dimmed">For degraded conditions when infrastructure may be impaired.</Text></Group>
          <Group gap={8}><Text span fw={700} size="xs" c="red" w={70}>Emergency</Text><Text span size="xs" c="dimmed">Last resort. Assume minimal or no infrastructure.</Text></Group>
        </Stack>
      </Paper>
    </div>
  );
}

function QuickLink({ icon, iconBg, title, subtitle, onClick }: {
  icon: React.ReactNode; iconBg: string; title: string; subtitle: string; onClick: () => void;
}) {
  return (
    <Paper p="md" withBorder style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 16 }}
      onClick={onClick}>
      <ThemeIcon size={40} radius="md" variant="light" style={{ background: iconBg }}>
        {icon}
      </ThemeIcon>
      <Box style={{ flex: 1 }}>
        <Text fw={600} size="sm">{title}</Text>
        <Text size="xs" c="dimmed">{subtitle}</Text>
      </Box>
      <IconArrowRight size={14} color="#9ca3af" />
    </Paper>
  );
}
